import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Download, ExternalLink, CheckCircle } from 'lucide-react';
import { invoicesAPI } from '../../services/api';
import { useState } from 'react';
import InvoicePayModal from '../../components/billing/InvoicePayModal';

import BackButton from '../../components/common/BackButton';
const getInvoiceBillTo = (invoice = {}) => {
  const billingParty = invoice?.billingParty || {};
  const fallbackAddress =
    billingParty?.type === 'facility' ? invoice?.facilityId?.address : invoice?.clientId?.address;
  return {
    name:
      billingParty?.name ||
      (billingParty?.type === 'facility'
        ? invoice?.facilityId?.name || invoice?.clientId?.name
        : invoice?.clientId?.name) ||
      '-',
    addressLines: [
      billingParty?.address?.street || fallbackAddress?.street,
      [
        billingParty?.address?.city || fallbackAddress?.city,
        billingParty?.address?.state || fallbackAddress?.state,
        billingParty?.address?.zip || fallbackAddress?.zip,
      ]
        .filter(Boolean)
        .join(', '),
    ].filter(Boolean),
    contactLines: [
      billingParty?.contactName,
      billingParty?.contactEmail || invoice?.billingEmail,
      billingParty?.contactPhone,
      billingParty?.apEmail ? `AP: ${billingParty.apEmail}` : null,
    ].filter(Boolean),
  };
};

export default function InvoiceDetailPage() {
  const { id } = useParams();

  const [showPay, setShowPay] = useState(false);
  const { data: invoice, isLoading, refetch } = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => invoicesAPI.getById(id),
    select: (res) => res.data.invoice,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="text-center py-12">
        <p className="text-secondary-500">Invoice not found</p>
      </div>
    );
  }

  const billTo = getInvoiceBillTo(invoice);

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <BackButton fallbackTo={"/invoices"} plainClassName="inline-flex items-center gap-2 text-secondary-600 hover:text-secondary-900" />

      <div className="card">
        {/* Header */}
        <div className="card-header flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-display font-bold text-secondary-900">
              Invoice {invoice.invoiceNumber}
            </h1>
            <p className="text-secondary-500 mt-1">
              {invoice.projectId?.name}
            </p>
          </div>
          <span className={`badge ${
            invoice.status === 'paid' ? 'badge-success' :
            invoice.status === 'overdue' ? 'badge-danger' :
            'badge-primary'
          }`}>
            {invoice.status}
          </span>
        </div>

        <div className="card-body">
          {/* Client & Dates */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pb-6 border-b border-secondary-200">
            <div>
              <p className="text-sm text-secondary-500">Bill To</p>
              <p className="font-medium text-secondary-900">{billTo.name}</p>
              {billTo.addressLines.length > 0 && (
                <p className="text-secondary-600 whitespace-pre-line">
                  {billTo.addressLines.join('\n')}
                </p>
              )}
              {billTo.contactLines.length > 0 && (
                <p className="text-secondary-500 whitespace-pre-line mt-1">
                  {billTo.contactLines.join('\n')}
                </p>
              )}
            </div>
            <div className="text-right">
              <div className="mb-2">
                <p className="text-sm text-secondary-500">Issue Date</p>
                <p className="font-medium text-secondary-900">
                  {new Date(invoice.issueDate).toLocaleDateString()}
                </p>
              </div>
              <div>
                <p className="text-sm text-secondary-500">Due Date</p>
                <p className="font-medium text-secondary-900">
                  {new Date(invoice.dueDate).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div className="py-6">
            <table className="w-full">
              <thead>
                <tr className="border-b border-secondary-200">
                  <th className="text-left py-2 text-sm text-secondary-500">Description</th>
                  <th className="text-right py-2 text-sm text-secondary-500">Qty</th>
                  <th className="text-right py-2 text-sm text-secondary-500">Rate</th>
                  <th className="text-right py-2 text-sm text-secondary-500">Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoice.items?.map((item, index) => (
                  <tr key={index} className="border-b border-secondary-100">
                    <td className="py-3 text-secondary-900">{item.description}</td>
                    <td className="py-3 text-right text-secondary-600">{item.quantity}</td>
                    <td className="py-3 text-right text-secondary-600">${item.unitPrice?.toLocaleString()}</td>
                    <td className="py-3 text-right font-medium text-secondary-900">${item.amount?.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end">
            <div className="w-64 space-y-2">
              <div className="flex justify-between">
                <span className="text-secondary-500">Subtotal</span>
                <span className="text-secondary-900">${invoice.subtotal?.toLocaleString()}</span>
              </div>
              {invoice.tax?.amount > 0 && (
                <div className="flex justify-between">
                  <span className="text-secondary-500">Tax ({invoice.tax.rate}%)</span>
                  <span className="text-secondary-900">${invoice.tax.amount?.toLocaleString()}</span>
                </div>
              )}
              {invoice.discount?.amount > 0 && (
                <div className="flex justify-between">
                  <span className="text-secondary-500">Discount</span>
                  <span className="text-green-600">-${invoice.discount.amount?.toLocaleString()}</span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t border-secondary-200 font-bold">
                <span className="text-secondary-900">Total</span>
                <span className="text-secondary-900">${invoice.total?.toLocaleString()}</span>
              </div>
              {invoice.amountPaid > 0 && (
                <div className="flex justify-between">
                  <span className="text-secondary-500">Paid</span>
                  <span className="text-green-600">-${invoice.amountPaid?.toLocaleString()}</span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t border-secondary-200 font-bold text-lg">
                <span className="text-secondary-900">Amount Due</span>
                <span className="text-primary-600">${invoice.amountDue?.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          {invoice.notes && (
            <div className="mt-6 pt-6 border-t border-secondary-200">
              <p className="text-sm text-secondary-500 mb-2">Notes</p>
              <p className="text-secondary-600">{invoice.notes}</p>
            </div>
          )}

          {/* Actions */}
          <div className="mt-8 flex flex-wrap gap-3">
            {invoice.amountDue > 0 && invoice.status !== 'paid' && (
              <>
                <button onClick={() => setShowPay(true)} className="btn-primary">
                  <ExternalLink size={18} />
                  Pay Now
                </button>
                {/* Keep Stripe-hosted link as fallback */}
                {invoice.stripePaymentLink && (
                  <a href={invoice.stripePaymentLink} target="_blank" rel="noopener noreferrer" className="ml-2 text-primary-600 hover:text-primary-700">
                    Pay with Stripe link
                  </a>
                )}
              </>
            )}
            <button className="btn-secondary">
              <Download size={18} />
              Download PDF
            </button>
          </div>

          {showPay && (
            <InvoicePayModal invoiceId={invoice._id} onClose={() => setShowPay(false)} onPaid={() => refetch()} />
          )}
        </div>
      </div>
    </div>
  );
}
